// ============================================================================
// The meta line under a facility name in the Accounts list.
//
// Moved out of my-accounts.tsx by CW-Q Phase 6 (2026-08-14) so it can be
// rendered and READ on its own. my-accounts.tsx cannot be imported in a node
// test — it pulls in the supabase client and @react-google-maps/api at module
// scope — and the rule this line has to obey is a negative one ("an hourly
// hospital must never render as a blank"), which can only be checked by looking
// at the output.
// ============================================================================

import { fmtCurrency } from '../../../shared/format';
import {
  clientRateDisplay,
  deriveRateBasisFallback,
  normalizeRateBasis,
} from '../../../shared/rate-basis';

export function FacilityMetaLine({
  am,
  monthlyRate,
  rateBasis,
  openFeedback,
  totalFeedback,
  hourlyRate = null,
}: {
  am: string | null;
  monthlyRate: number | null;
  /**
   * Items 831/758: the one hourly rate the building's running lines billed
   * by the hour carry. An hourly building then reads "Billed on actual hours —
   * $26 an hour"; with none (Menorah) just "Billed on actual hours".
   */
  hourlyRate?: number | null;
  /**
   * CW-Q Phase 6. Three states, all meaningful:
   *   string    → v_facility_margins.rate_basis for this facility.
   *   null      → real facility, no view row (Closed/Merged) or the read
   *               failed → derive what the raw rate can prove. Never 'hourly'.
   *   undefined → the caller has nothing to say about the basis. Say nothing
   *               about rates at all, exactly as before this phase. (Held
   *               for callers outside the AM list; the "Pending setup" ghost
   *               card that first needed it retired on 2026-09-11.)
   */
  rateBasis: string | null | undefined;
  openFeedback: number;
  totalFeedback: number;
}) {
  // fmtCurrency is kept for the amount so the 32 facilities with cents keep
  // rendering rounded here, as they always have — the card shows cents, the
  // list shows dollars, and this phase is not the place to change that.
  const display =
    rateBasis === undefined
      ? null
      : clientRateDisplay({
          basis:
            rateBasis === null
              ? deriveRateBasisFallback(monthlyRate)
              : normalizeRateBasis(rateBasis),
          monthlyRate,
          hourlyRate,
        });
  const rate =
    display == null ? '' : display.isAmount ? `${fmtCurrency(monthlyRate)}/mo` : display.text;
  const rateIsAmount = display?.isAmount === true;
  return (
    <>
      {am && <span>AM: {am}</span>}
      {rate && (
        <>
          {am && <span aria-hidden="true">·</span>}
          {/* The "/mo" now lives inside `rate` — appending it unconditionally is
              what would have produced "Billed on actual hours/mo". Monospace
              only for figures; a basis is prose. */}
          <span className={rateIsAmount ? 'font-mono' : 'text-tcs-text-muted'}>{rate}</span>
        </>
      )}
      {/* CW-I Phase 2. Was `openFeedback > 0` only, so a facility whose
          feedback is entirely Closed rendered nothing here and looked exactly
          like a facility with no feedback at all. That is the list-view half
          of why FBK-0254 went unnoticed. Open still gets the emphasis; closed
          history now at least announces itself. */}
      {openFeedback > 0 ? (
        <>
          {(am || rate) && <span aria-hidden="true">·</span>}
          <span className="font-medium text-tcs-text">
            {openFeedback} open feedback
          </span>
        </>
      ) : (
        totalFeedback > 0 && (
          <>
            {(am || rate) && <span aria-hidden="true">·</span>}
            <span className="text-tcs-text-muted">
              {totalFeedback} feedback
            </span>
          </>
        )
      )}
    </>
  );
}
